import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Users, DollarSign, TrendingUp, Activity, LogOut,
  Search, RefreshCw, Eye, Lock, CheckCircle, XCircle, AlertCircle,
  BarChart2, ChevronDown, ChevronUp, X, ToggleLeft, ToggleRight,
  Settings, User as UserIcon, Hash, Sliders
} from 'lucide-react';
import { toast } from 'react-toastify';
import { addNotification, getAllNotifications } from '../utils/notifications';

const API = 'https://tradebot-k4nt.onrender.com/api';
const ADMIN_EMAILS = ['michaelawofeko@gmail.com', 'michaeloluwatosin321@gmail.com', 'victorralph407@gmail.com'];

// ─── Feature definitions ─────────────────────────────────────────────────────
const ALL_FEATURES = [
  { key: 'send_money',        label: 'Send Money',         description: 'Allow users to send funds to others' },
  { key: 'receive_money',     label: 'Receive Money',      description: 'Allow users to generate receive QR/links' },
  { key: 'withdraw',          label: 'Withdraw',           description: 'Allow users to withdraw to bank/card' },
  { key: 'deposit',           label: 'Deposit',            description: 'Allow users to deposit funds' },
  { key: 'pene_dashboard',    label: 'PENE Dashboard',     description: 'Access to PENE investment dashboard' },
  { key: 'pene_exchange',     label: 'PENE Exchange',      description: 'Allow buying/selling PENE' },
  { key: 'pene_transfer',     label: 'PENE Transfer',      description: 'Allow sending PENE to other users' },
  { key: 'subscription_plans',label: 'Subscription Plans', description: 'Access to investment subscription plans' },
  { key: 'payment_link',      label: 'Payment Link',       description: 'Generate and share payment links' },
  { key: 'history',           label: 'Transaction History', description: 'View transaction history' },
  { key: 'referral',          label: 'Referral Program',   description: 'Access referral dashboard & links' },
  { key: 'connect_account',   label: 'Connect Bank Account', description: 'Link external bank/Stripe accounts' },
  { key: 'crypto',            label: 'Crypto (Send/Receive)', description: 'Cryptocurrency send/receive features' },
];

// Local storage key for persisting feature flags
const STORAGE_KEY = 'modanmic_feature_flags';

const loadFlags = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
};
const saveFlags = (flags) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
};

// ─── Admin Login ─────────────────────────────────────────────────────────────
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setTimeout(() => {
      if (!ADMIN_EMAILS.includes(email.trim().toLowerCase())) {
        setError('Access denied. This email is not authorised.');
        setLoading(false);
        return;
      }
      if (password.length < 6) {
        setError('Invalid password.');
        setLoading(false);
        return;
      }
      localStorage.setItem('admin_session', JSON.stringify({ email: email.trim().toLowerCase(), ts: Date.now() }));
      onLogin(email.trim().toLowerCase());
      setLoading(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5"
           style={{ backgroundImage: 'linear-gradient(rgba(239,68,68,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(239,68,68,0.4) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-900/10 rounded-full blur-3xl" />
      <div className="w-full max-w-sm relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-900 rounded-2xl mb-5 shadow-[0_0_40px_rgba(185,28,28,0.5)]">
            <Shield className="text-red-300" size={30} />
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight">Admin Portal</h1>
          <p className="text-slate-500 text-sm mt-1">Restricted access — authorised personnel only</p>
        </div>
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-8 shadow-2xl">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Admin Email</label>
              <input type="email" placeholder="admin@email.com" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3.5 bg-slate-800 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all" required />
            </div>
            <div className="relative">
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Password</label>
              <input type={showPass ? 'text' : 'password'} placeholder="Admin password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3.5 bg-slate-800 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all pr-12" required />
              <button type="button" onClick={() => setShowPass(!showPass)}
                className="absolute right-4 top-[46px] text-slate-400 hover:text-white">
                {showPass ? <Eye size={18} /> : <Lock size={18} />}
              </button>
            </div>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-800 rounded-xl text-red-300 text-sm">
                <XCircle size={16} className="flex-shrink-0" />{error}
              </div>
            )}
            <button type="submit" disabled={loading}
              className="w-full py-4 bg-red-700 hover:bg-red-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Verifying...</> : <><Shield size={18} />Access Admin Panel</>}
            </button>
          </form>
        </div>
        <p className="text-center text-slate-600 text-xs mt-4">Access via Shift+A on any page</p>
      </div>
    </div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color = 'indigo' }) => {
  const colors = { indigo: 'bg-indigo-100 text-indigo-600', green: 'bg-green-100 text-green-600', amber: 'bg-amber-100 text-amber-600', red: 'bg-red-100 text-red-600', purple: 'bg-purple-100 text-purple-600' };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colors[color]}`}><Icon size={22} /></div>
      </div>
      <p className="text-2xl font-black text-slate-900">{value}</p>
      <p className="text-sm font-semibold text-slate-700 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
};

// ─── User Row ─────────────────────────────────────────────────────────────────
const UserRow = ({ user, onView }) => {
  const balance = user.accountBalance || '$0.00';
  const pene = parseFloat(user.investmentBalance || 0);
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {(user.telegramName || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-slate-900 text-sm">@{user.telegramName}</p>
            <p className="text-xs text-slate-400">{user.email || 'No email'}</p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-sm font-semibold text-slate-800">{balance}</td>
      <td className="py-3 px-4 text-sm text-purple-700 font-medium">{pene.toLocaleString()} PENE</td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${user.validated === 'verified' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
          {user.validated === 'verified' ? <CheckCircle size={11} /> : <AlertCircle size={11} />}
          {user.validated === 'verified' ? 'Verified' : 'Unverified'}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-slate-400">{user.dateCreated ? new Date(user.dateCreated).toLocaleDateString() : '-'}</td>
      <td className="py-3 px-4">
        <button onClick={() => onView(user)} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 px-2.5 py-1 rounded-lg hover:bg-indigo-50 transition">View</button>
      </td>
    </tr>
  );
};

// ─── Feature Toggle Panel ─────────────────────────────────────────────────────
const FeatureTogglePanel = ({ users }) => {
  const [mode, setMode] = useState('global'); // 'global' | 'user' | 'range'
  const [flags, setFlags] = useState(loadFlags);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [rangeSearch, setRangeSearch] = useState('');

  // flags structure:
  //   flags.global = { feature_key: true/false }
  //   flags.users  = { username: { feature_key: true/false } }

  const globalFlags = flags.global || {};
  const userFlags   = flags.users  || {};

  const persist = (newFlags) => {
    setFlags(newFlags);
    saveFlags(newFlags);
    toast.success('Feature settings saved');
  };

  // ── Global toggle ──
  const toggleGlobal = (key) => {
    const updated = {
      ...flags,
      global: { ...globalFlags, [key]: !(globalFlags[key] !== false) }
    };
    persist(updated);
  };

  const isGlobalOn = (key) => globalFlags[key] !== false; // default ON

  // ── Per-user toggle ──
  const toggleUserFeature = (username, key) => {
    const current = (userFlags[username] || {})[key];
    // cycle: inherit (undefined) → on (true) → off (false) → inherit (delete)
    let next;
    if (current === undefined) next = true;
    else if (current === true) next = false;
    else next = undefined;

    const userObj = { ...(userFlags[username] || {}), [key]: next };
    if (next === undefined) delete userObj[key];

    const updated = { ...flags, users: { ...userFlags, [username]: userObj } };
    persist(updated);
  };

  const getUserState = (username, key) => {
    const u = userFlags[username] || {};
    if (u[key] !== undefined) return u[key];
    return isGlobalOn(key); // falls back to global
  };

  const filteredUsers = users.filter(u =>
    (u.telegramName || '').toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
  );

  const rangeUsers = users.filter(u =>
    (u.telegramName || '').toLowerCase().includes(rangeSearch.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(rangeSearch.toLowerCase())
  );

  const toggleSelectUser = (username) => {
    setSelectedUsers(prev => prev.includes(username) ? prev.filter(u => u !== username) : [...prev, username]);
  };

  const applyToRange = (key, value) => {
    const usersUpdate = { ...userFlags };
    selectedUsers.forEach(username => {
      usersUpdate[username] = { ...(usersUpdate[username] || {}), [key]: value };
    });
    persist({ ...flags, users: usersUpdate });
  };

  const resetUserOverrides = (username) => {
    const usersUpdate = { ...userFlags };
    delete usersUpdate[username];
    persist({ ...flags, users: usersUpdate });
    toast.info(`Overrides cleared for @${username}`);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
      {/* Panel Header */}
      <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-indigo-900">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
            <Sliders className="text-white" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Feature Controls</h2>
            <p className="text-slate-400 text-xs">Enable or disable menu features globally or per user</p>
          </div>
        </div>
        {/* Mode Tabs */}
        <div className="flex gap-2">
          {[
            { id: 'global', icon: Settings, label: 'Global' },
            { id: 'user',   icon: UserIcon, label: 'Per User' },
            { id: 'range',  icon: Hash,     label: 'User Range' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setMode(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${mode === tab.id ? 'bg-white text-slate-900' : 'text-slate-300 hover:bg-white/10'}`}>
              <tab.icon size={15} />{tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">

        {/* ── GLOBAL MODE ── */}
        {mode === 'global' && (
          <div>
            <p className="text-sm text-slate-500 mb-5">These settings apply to <strong>all users</strong> unless overridden per-user. Features are ON by default.</p>
            <div className="grid md:grid-cols-2 gap-3">
              {ALL_FEATURES.map(f => {
                const on = isGlobalOn(f.key);
                return (
                  <div key={f.key} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${on ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{f.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{f.description}</p>
                    </div>
                    <button onClick={() => toggleGlobal(f.key)} className="ml-4 flex-shrink-0">
                      {on
                        ? <ToggleRight size={36} className="text-green-500" />
                        : <ToggleLeft  size={36} className="text-slate-300" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── PER USER MODE ── */}
        {mode === 'user' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Search a user and toggle features individually. <span className="font-medium text-indigo-600">Inherit</span> = follows global setting.</p>
            <div className="relative mb-5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input type="text" placeholder="Search user by name or email..."
                value={userSearch} onChange={e => setUserSearch(e.target.value)}
                className="pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm w-full" />
            </div>
            {userSearch && (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {filteredUsers.slice(0, 5).map(u => {
                  const username = u.telegramName;
                  const hasOverrides = Object.keys(userFlags[username] || {}).length > 0;
                  return (
                    <div key={username} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                            {username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900 text-sm">@{username}</p>
                            <p className="text-xs text-slate-400">{u.email || 'No email'}</p>
                          </div>
                        </div>
                        {hasOverrides && (
                          <button onClick={() => resetUserOverrides(username)}
                            className="text-xs text-red-500 hover:text-red-700 border border-red-200 px-2.5 py-1 rounded-lg hover:bg-red-50 transition">
                            Reset overrides
                          </button>
                        )}
                      </div>
                      <div className="p-3 grid md:grid-cols-2 gap-2">
                        {ALL_FEATURES.map(f => {
                          const state = getUserState(username, f.key);
                          const override = (userFlags[username] || {})[f.key];
                          const isInherited = override === undefined;
                          return (
                            <button key={f.key} onClick={() => toggleUserFeature(username, f.key)}
                              className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                                isInherited ? 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                                : state ? 'border-green-200 bg-green-50 hover:bg-green-100'
                                : 'border-red-200 bg-red-50 hover:bg-red-100'
                              }`}>
                              <div>
                                <p className="text-xs font-semibold text-slate-800">{f.label}</p>
                                <p className={`text-xs mt-0.5 font-medium ${isInherited ? 'text-slate-400' : state ? 'text-green-600' : 'text-red-500'}`}>
                                  {isInherited ? 'Inherit (global)' : state ? 'Forced ON' : 'Forced OFF'}
                                </p>
                              </div>
                              {state
                                ? <ToggleRight size={28} className={isInherited ? 'text-slate-300' : 'text-green-500'} />
                                : <ToggleLeft  size={28} className="text-slate-300" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {filteredUsers.length === 0 && <p className="text-center text-slate-400 py-8">No users found</p>}
              </div>
            )}
            {!userSearch && <p className="text-center text-slate-300 py-10 text-sm">Start typing a username above to manage their features</p>}
          </div>
        )}

        {/* ── RANGE MODE ── */}
        {mode === 'range' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Select multiple users, then bulk-toggle specific features across all of them at once.</p>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input type="text" placeholder="Filter users to select..."
                value={rangeSearch} onChange={e => setRangeSearch(e.target.value)}
                className="pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm w-full" />
            </div>

            {/* User Selector */}
            <div className="border border-slate-200 rounded-xl overflow-hidden mb-5">
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <p className="text-xs font-semibold text-slate-600">{selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected</p>
                <div className="flex gap-2">
                  <button onClick={() => setSelectedUsers(rangeUsers.map(u => u.telegramName))}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Select all</button>
                  <span className="text-slate-300">|</span>
                  <button onClick={() => setSelectedUsers([])}
                    className="text-xs text-slate-500 hover:text-slate-700 font-medium">Clear</button>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {rangeUsers.slice(0, 50).map(u => {
                  const sel = selectedUsers.includes(u.telegramName);
                  return (
                    <label key={u.telegramName} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${sel ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}>
                      <input type="checkbox" checked={sel} onChange={() => toggleSelectUser(u.telegramName)} className="rounded border-slate-300 text-indigo-600" />
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                        {u.telegramName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">@{u.telegramName}</p>
                        <p className="text-xs text-slate-400">{u.email || 'No email'}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Bulk Feature Toggles */}
            {selectedUsers.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-3">Apply feature to selected {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}:</p>
                <div className="grid md:grid-cols-2 gap-2">
                  {ALL_FEATURES.map(f => (
                    <div key={f.key} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50">
                      <div>
                        <p className="text-xs font-semibold text-slate-800">{f.label}</p>
                      </div>
                      <div className="flex gap-2 ml-3">
                        <button onClick={() => applyToRange(f.key, true)}
                          className="text-xs px-2.5 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-semibold transition">ON</button>
                        <button onClick={() => applyToRange(f.key, false)}
                          className="text-xs px-2.5 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 font-semibold transition">OFF</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {selectedUsers.length === 0 && <p className="text-center text-slate-300 py-6 text-sm">Select users above to apply bulk changes</p>}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Admin Dashboard ─────────────────────────────────────────────────────
const AdminDashboardInner = ({ adminEmail, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [notificationMode, setNotificationMode] = useState('all');
  const [notificationSubject, setNotificationSubject] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSearch, setNotificationSearch] = useState('');
  const [selectedNotificationUsers, setSelectedNotificationUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [stats, setStats] = useState({ totalUsers: 0, totalBalance: 0, totalPene: 0, verified: 0 });
  const [sortField, setSortField] = useState('dateCreated');
  const [sortDir, setSortDir] = useState('desc');
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'features'

  const fetchAllUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/users/ADMIN`);
      const data = await res.json();
      // API returns { usernameInfo: [...] } shape
      const all = data?.usernameInfo || data?.mainUser || [];
      if (all.length > 0) {
        setUsers(all);
        let tb = 0, tp = 0, tv = 0;
        all.forEach(u => {
          tb += parseFloat((u.accountBalance || '$0').replace(/[^0-9.]/g, '')) || 0;
          tp += parseFloat(u.investmentBalance || 0) || 0;
          if (u.validated === 'verified' || u.validated === 'true') tv++;
        });
        setStats({ totalUsers: all.length, totalBalance: tb, totalPene: tp, verified: tv });
      }
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const data = await getAllNotifications();
      setNotifications(data || []);
    } catch (e) {
      toast.error('Failed to load notifications');
      console.error('fetchAllNotifications error:', e);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllUsers();
    fetchAllNotifications();
  }, [fetchAllUsers, fetchAllNotifications]);

  const filtered = users.filter(u =>
    (u.telegramName || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const notificationFilteredUsers = users.filter(u =>
    (u.telegramName || '').toLowerCase().includes(notificationSearch.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(notificationSearch.toLowerCase())
  );

  const toggleNotificationUser = (username) => {
    setSelectedNotificationUsers((prev) =>
      prev.includes(username) ? prev.filter((item) => item !== username) : [...prev, username]
    );
  };

  const handleSendNotification = async () => {
    if (!notificationSubject.trim() || !notificationMessage.trim()) {
      toast.error('Subject and message are required.');
      return;
    }
    if (notificationMode === 'users' && selectedNotificationUsers.length === 0) {
      toast.error('Select at least one recipient for user-specific notifications.');
      return;
    }
    try {
      await addNotification({
        subject: notificationSubject.trim(),
        message: notificationMessage.trim(),
        targetType: notificationMode === 'all' ? 'all' : 'users',
        recipients: notificationMode === 'all' ? [] : selectedNotificationUsers,
        createdBy: adminEmail,
      });
      toast.success('Notification sent successfully');
      setNotificationSubject('');
      setNotificationMessage('');
      setSelectedNotificationUsers([]);
      setNotificationSearch('');
      await fetchAllNotifications();
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification. Please try again.');
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortField] || '', bv = b[sortField] || '';
    if (sortField === 'accountBalance') {
      av = parseFloat((av || '$0').replace(/[^0-9.]/g, '')) || 0;
      bv = parseFloat((bv || '$0').replace(/[^0-9.]/g, '')) || 0;
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const SortIcon = ({ field }) => sortField === field ? (sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Nav */}
      <nav className="bg-slate-950 border-b border-slate-800 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-red-700 rounded-xl flex items-center justify-center">
            <Shield className="text-white" size={18} />
          </div>
          <div>
            <h1 className="text-white font-black text-base leading-tight">MODANMIC Admin</h1>
            <p className="text-slate-500 text-xs">{adminEmail}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Tab Buttons */}
          <button onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl transition text-sm font-semibold ${activeTab === 'users' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
            <Users size={15} /> Users
          </button>
          <button onClick={() => setActiveTab('features')}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl transition text-sm font-semibold ${activeTab === 'features' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
            <Sliders size={15} /> Features
          </button>
          <div className="w-px h-6 bg-slate-700 mx-1" />
          <button onClick={fetchAllUsers} className="flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition text-sm">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition text-sm">
            <Activity size={15} /> Live App
          </button>
          <button onClick={onLogout} className="flex items-center gap-2 px-3 py-2 bg-red-900/40 text-red-300 hover:bg-red-900/70 rounded-xl transition text-sm font-semibold">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Stats Row — always visible */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users}       label="Total Users"       value={stats.totalUsers.toLocaleString()} color="indigo" />
          <StatCard icon={DollarSign}  label="Total Balances"    value={`$${stats.totalBalance.toLocaleString()}`} color="green" />
          <StatCard icon={TrendingUp}  label="Total PENE"        value={stats.totalPene.toLocaleString()} color="purple" />
          <StatCard icon={CheckCircle} label="Verified Accounts" value={stats.verified} sub={`${stats.totalUsers ? Math.round(stats.verified / stats.totalUsers * 100) : 0}% of total`} color="amber" />
        </div>

        {/* Feature Toggle Panel */}
        {activeTab === 'features' && <FeatureTogglePanel users={users} />}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-5 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-bold text-slate-900">Send Admin Notifications</h2>
            <p className="text-sm text-slate-500 mt-1">Send a message to all users or select users individually.</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {['all', 'users'].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setNotificationMode(option)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition ${notificationMode === option ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {option === 'all' ? 'All Users' : 'Select Users'}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={notificationSubject}
              onChange={(e) => setNotificationSubject(e.target.value)}
              placeholder="Notification subject"
              className="w-full px-4 py-3 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            />
            <textarea
              rows={4}
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              placeholder="Notification message"
              className="w-full px-4 py-3 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-none"
            />
            {notificationMode === 'users' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    value={notificationSearch}
                    onChange={(e) => setNotificationSearch(e.target.value)}
                    placeholder="Search users to target"
                    className="w-full pl-9 pr-4 py-3 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto border border-slate-200 rounded-2xl p-3">
                  {notificationFilteredUsers.slice(0, 20).map((u) => {
                    const selected = selectedNotificationUsers.includes(u.telegramName);
                    return (
                      <button
                        key={u.telegramName}
                        type="button"
                        onClick={() => toggleNotificationUser(u.telegramName)}
                        className={`text-left rounded-2xl px-3 py-2 border ${selected ? 'border-indigo-500 bg-indigo-50 text-slate-900' : 'border-slate-200 bg-white text-slate-700'} text-sm`}
                      >
                        @{u.telegramName}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={handleSendNotification}
              className="inline-flex items-center justify-center px-6 py-3 bg-slate-900 text-white rounded-2xl font-semibold hover:bg-slate-800 transition"
            >
              Send Notification
            </button>
          </div>
        </div>

        {/* Notification History */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-5 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-bold text-slate-900">Notification History</h2>
            <p className="text-sm text-slate-500 mt-1">Track all notifications sent from the admin dashboard.</p>
          </div>
          <div className="p-6">
            {notificationsLoading ? (
              <div className="text-center py-12 text-slate-500">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12 text-slate-500">No notifications have been sent yet.</div>
            ) : (
              <div className="space-y-4">
                {notifications.map((note) => (
                  <div key={note.id || `${note.createdAt}-${note.subject}`} className="rounded-3xl border border-slate-200 p-4 bg-slate-50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{note.subject}</p>
                        <p className="text-sm text-slate-500 mt-1">{note.message}</p>
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <p>{new Date(note.createdAt).toLocaleString()}</p>
                        <p className="mt-1">{note.targetType === 'all' ? 'All users' : `Users: ${note.recipients?.length || 0}`}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-600">
                      <span className="inline-flex items-center rounded-full bg-white border border-slate-200 px-2 py-1">Created by: {note.createdBy || 'admin'}</span>
                      {note.targetType === 'users' && note.recipients?.length > 0 && (
                        <span className="inline-flex items-center rounded-full bg-white border border-slate-200 px-2 py-1">Recipients: {note.recipients.join(', ')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Users Table */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-bold text-slate-900">All Users</h2>
                <p className="text-sm text-slate-500">{filtered.length} user{filtered.length !== 1 ? 's' : ''} found</p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input type="text" placeholder="Search username or email..." value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm w-64" />
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
              </div>
            ) : sorted.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <Users size={40} className="mx-auto mb-3 opacity-30" />
                <p>No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-800" onClick={() => toggleSort('telegramName')}>
                        <span className="flex items-center gap-1">User <SortIcon field="telegramName" /></span>
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-800" onClick={() => toggleSort('accountBalance')}>
                        <span className="flex items-center gap-1">Balance <SortIcon field="accountBalance" /></span>
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">PENE</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-800" onClick={() => toggleSort('dateCreated')}>
                        <span className="flex items-center gap-1">Joined <SortIcon field="dateCreated" /></span>
                      </th>
                      <th className="py-3 px-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((u, i) => <UserRow key={u._id || i} user={u} onView={setSelectedUser} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-slate-400 text-xs mt-6">
          MODANMIC Admin Panel · {adminEmail} · {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-7 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setSelectedUser(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100">
              <X size={18} />
            </button>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-black text-2xl">
                {(selectedUser.telegramName || '?').charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900">@{selectedUser.telegramName}</h2>
                <p className="text-slate-500 text-sm">{selectedUser.email || 'No email'}</p>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${selectedUser.validated === 'verified' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {selectedUser.validated === 'verified' ? <CheckCircle size={11} /> : <AlertCircle size={11} />}
                  {selectedUser.validated === 'verified' ? 'Verified' : 'Unverified'}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Account Balance',  value: selectedUser.accountBalance || '$0.00' },
                { label: 'PENE Balance',      value: `${parseFloat(selectedUser.investmentBalance || 0).toLocaleString()} PENE` },
                { label: 'Referral Balance',  value: `$${parseFloat(selectedUser.refralBalance || 0).toFixed(2)}` },
                { label: 'Referrals',         value: (selectedUser.refralList || []).length },
                { label: 'Member Since',      value: selectedUser.dateCreated ? new Date(selectedUser.dateCreated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '-' },
                { label: 'Last Deposit',      value: selectedUser.lastDepsit || 'None' },
                { label: 'Full Name',         value: selectedUser.fullName || '-' },
                { label: 'Phone',             value: selectedUser.phone || '-' },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-500">{row.label}</span>
                  <span className="text-sm font-semibold text-slate-900">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Root ─────────────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const [adminEmail, setAdminEmail] = useState(() => {
    try {
      const session = JSON.parse(localStorage.getItem('admin_session') || 'null');
      if (!session) return null;
      if (Date.now() - session.ts > 8 * 60 * 60 * 1000) { localStorage.removeItem('admin_session'); return null; }
      if (!ADMIN_EMAILS.includes(session.email)) return null;
      return session.email;
    } catch { return null; }
  });

  const handleLogout = () => {
    localStorage.removeItem('admin_session');
    setAdminEmail(null);
    toast.success('Logged out from admin panel');
  };

  if (!adminEmail) return <AdminLogin onLogin={setAdminEmail} />;
  return <AdminDashboardInner adminEmail={adminEmail} onLogout={handleLogout} />;
};

export default AdminDashboard;
